import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { v4 as uuidV4 } from 'uuid'
import type { Moment } from 'moment'
import type { SetOptional } from 'type-fest'

import { DATE_SEARCH_OPTION_IDS, type FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import { fetchCruiseOffer } from 'actions/CruiseActions'
import { streamOfferList, updateOfferListKey } from 'actions/OfferActions'
import { streamTypeahead } from 'actions/SearchActions'
import { useTypeaheadStreaming } from 'hooks/Search/useTypeaheadStreaming'
import useOffers from 'hooks/Offers/useOffers'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useStableObjectReference from 'hooks/useStableObjectReference'
import getOfferListKey from 'lib/offer/offerListKey'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import { allRoomsOccupantsFitRoomCapacity } from 'lib/checkout/cartReservationUtils'
import useExperienceList from 'hooks/Experiences/useExperienceList'
import { convertFlexibleDatesToTravelDates } from 'lib/search/searchUtils'
import type { BatchOfferDetailResult } from 'tripPlanner/api/types'
import useOfferDetailBatch from './useOfferDetailBatch'
import { mapOfferDetailToCarouselOffer } from './mapOfferDetailToCarouselOffer'

type CarouselOfferProduct = 'hotel' | 'experience' | 'cruise'

/**
 * The batch endpoint's own inline `detail`, not the single-offer endpoint's result. The two
 * are identical today because they generate from the same schema, but naming the batch one
 * here means a future divergence fails at this line instead of silently type-checking.
 */
type BatchOfferDetail = Extract<BatchOfferDetailResult, { status: 'ok' }>['detail']

/**
 * The batch's own reason for a per-offer failure. Named off the contract for the same reason
 * as `BatchOfferDetail` above: a widened `string` would silently accept a reason the backend
 * has since dropped, instead of failing at this line.
 */
export type BatchUnavailableReason = Extract<BatchOfferDetailResult, { status: 'error' }>['reason']

interface UseCarouselOfferSearchArgs {
  searchQuery?: string
  cardType?: CarouselOfferProduct
  rooms: Array<App.Occupants>
  filterActive: boolean
  hasUserEditedFilters?: boolean
  dateMode: DATE_SEARCH_OPTION_IDS
  checkIn?: Moment
  checkOut?: Moment
  flexibleMonths: string
  flexibleNights?: FLEXIBLE_DURATION_RANGE
  originalOfferIds: Array<string>
}

const emptyOfferList: SetOptional<App.OfferList, 'key'> = {
  offerIds: [],
  fetching: true,
  error: null,
}

const emptyDisabledOfferList: SetOptional<App.OfferList, 'key'> = {
  offerIds: [],
  fetching: false,
  error: null,
}

function useCruiseCarouselOffers(offerIds: Array<string>, enabled: boolean) {
  const dispatch = useAppDispatch()
  const stableOfferIds = useStableObjectReference(offerIds)

  useEffect(() => {
    if (!enabled) return
    stableOfferIds.forEach((offerId) => dispatch(fetchCruiseOffer(offerId)))
  }, [dispatch, enabled, stableOfferIds])

  return useAppSelector((state) => {
    const offers = stableOfferIds
      .map((offerId) => state.cruise.cruiseOffers[offerId])
      .filter((offer): offer is App.CruiseOffer => !!offer)

    const isResolved = (offerId: string) =>
      !!state.cruise.cruiseOffers[offerId] || !!state.cruise.cruiseOffersErrors[offerId]

    return {
      offers,
      fetching: enabled && stableOfferIds.some((offerId) =>
        state.cruise.cruiseOffersLoading[offerId] || !isResolved(offerId),
      ),
      resolvedCount: stableOfferIds.filter(isResolved).length,
    }
  }, shallowEqual)
}

function useCarouselHotelOfferList(filters: App.OfferListFilters | undefined, typeaheadId: string): App.OfferList {
  const dispatch = useAppDispatch()
  const listenerId = useId()
  const searchDisabled = !filters
  const listKey = useMemo(() => filters ? getOfferListKey(filters) : '', [filters])

  useEffect(() => {
    if (searchDisabled || !filters) return

    dispatch(streamOfferList(filters, listenerId, undefined, undefined, {
      typeaheadId,
    }))
  }, [dispatch, filters, listenerId, searchDisabled, typeaheadId])

  useEffect(() => {
    return () => {
      if (!searchDisabled) {
        dispatch(updateOfferListKey({ offerListKey: listKey, listenerId }))
      }
    }
  }, [dispatch, listKey, listenerId, searchDisabled])

  const offerList = useAppSelector((state) => {
    if (searchDisabled) return emptyDisabledOfferList
    return state.offer.offerLists[listKey] ?? emptyOfferList
  })

  const metaData = useAppSelector(state => state.offer.searchResultMetadata.offerMetaData[listKey])

  return useMemo(() => ({
    ...offerList,
    key: offerList.key ?? listKey,
    offerIds: metaData ? offerList.offerIds.filter(id => {
      const offerData = metaData[id]
      return !offerData || offerData.available
    }) : offerList.offerIds,
  }), [listKey, metaData, offerList])
}

export default function useCarouselOfferSearch({
  searchQuery,
  cardType = 'hotel',
  rooms,
  filterActive,
  hasUserEditedFilters = false,
  dateMode,
  checkIn,
  checkOut,
  flexibleMonths,
  flexibleNights,
  originalOfferIds,
}: UseCarouselOfferSearchArgs) {
  const [typeaheadId] = useState(() => uuidV4())
  const dispatch = useAppDispatch()
  const listenerId = useId()
  const canSearchHotels = cardType === 'hotel' && !!searchQuery && hasUserEditedFilters

  const { dropdownData, naturalLanguageItemData } = useTypeaheadStreaming(searchQuery || '', typeaheadId, {
    enabled: canSearchHotels,
    debounceMs: 0,
  })

  const [listingTypeaheadId] = useState(() => uuidV4())
  const { dropdownData: listingDropdownData, naturalLanguageItemData: listingItemData } = useTypeaheadStreaming(searchQuery || '', listingTypeaheadId, { enabled: false })
  const resolveListingDestination = useCallback(() => {
    if (!searchQuery || cardType !== 'hotel') return
    dispatch(streamTypeahead({ input: searchQuery, typeaheadId: listingTypeaheadId, listenerId }))
  }, [dispatch, searchQuery, cardType, listingTypeaheadId, listenerId])
  const listingLocation = listingItemData?.location?.data
  const listingPlaceIds = listingLocation && 'placeIds' in listingLocation ? listingLocation.placeIds : undefined
  const extractedLocation = naturalLanguageItemData?.location?.data
  const extractedPlaceIds = extractedLocation && 'placeIds' in extractedLocation
    ? extractedLocation.placeIds
    : undefined

  const typeaheadResolved = canSearchHotels && !!naturalLanguageItemData && !!dropdownData?.isComplete
  // A streaming error is terminal: `isComplete` never flips true, so without this the
  // carousel would keep showing loading skeletons forever. Treat it as "give up on the
  // NLS search and fall back to the carousel's original offers".
  const typeaheadError = canSearchHotels && !!dropdownData?.streamingError
  const shouldSearchHotelResults = cardType === 'hotel' && hasUserEditedFilters
  const experienceAvailabilityRange = useMemo(() => {
    if (cardType !== 'experience' || !filterActive) return undefined

    if (dateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC && checkIn && checkOut) {
      return {
        from: checkIn.format(ISO_DATE_FORMAT),
        to: checkOut.format(ISO_DATE_FORMAT),
      }
    }

    if (dateMode === DATE_SEARCH_OPTION_IDS.FLEXIBLE && flexibleMonths && flexibleNights) {
      const flexibleDates = convertFlexibleDatesToTravelDates(flexibleNights, flexibleMonths)
      return {
        from: flexibleDates.checkIn,
        to: flexibleDates.checkOut,
      }
    }

    return undefined
  }, [cardType, checkIn, checkOut, dateMode, filterActive, flexibleMonths, flexibleNights])
  const shouldFilterExperienceResults = cardType === 'experience' && !!experienceAvailabilityRange

  const nlsFilters = useMemo<App.OfferListFilters | undefined>(() => {
    if (!shouldSearchHotelResults || !extractedPlaceIds?.length) return undefined

    const filters: App.OfferListFilters = {
      destinationId: extractedPlaceIds[0],
      destinationIds: extractedPlaceIds,
      rooms,
      offerTypes: [OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK],
      limit: 10,
      isStream: true,
    }

    if (dateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC && checkIn && checkOut) {
      filters.checkIn = checkIn.format(ISO_DATE_FORMAT)
      filters.checkOut = checkOut.format(ISO_DATE_FORMAT)
    } else if (dateMode === DATE_SEARCH_OPTION_IDS.FLEXIBLE) {
      if (flexibleMonths) filters.flexibleMonths = flexibleMonths
      if (flexibleNights) filters.flexibleNights = flexibleNights
    }

    return filters
  }, [checkIn, checkOut, dateMode, extractedPlaceIds, flexibleMonths, flexibleNights, rooms, shouldSearchHotelResults])

  const nlsOfferList = useCarouselHotelOfferList(nlsFilters, typeaheadId)
  const shouldUseHotelSearchResults = shouldSearchHotelResults && !!nlsFilters && !typeaheadError
  const shouldFallbackToOriginalHotelOffers = shouldSearchHotelResults &&
    ((typeaheadResolved && !extractedPlaceIds?.length) || typeaheadError)
  const experienceFilterOfferIds = useStableObjectReference(shouldFilterExperienceResults ? originalOfferIds : [])
  const [experienceAvailabilityList] = useExperienceList({
    experienceIds: experienceFilterOfferIds,
    from: experienceAvailabilityRange?.from,
    to: experienceAvailabilityRange?.to,
  })
  const hasExperienceAvailabilityList = useAppSelector((state) =>
    !!state.experience.experienceLists[experienceAvailabilityList.listKey],
  )
  const availableExperienceIds = useMemo(
    () => new Set(experienceAvailabilityList.ids),
    [experienceAvailabilityList.ids],
  )

  // Stabilised at the source, like `offerIds` above and `experienceFilterOfferIds` below.
  // The parent re-creates this array on renders that do not change its contents, and
  // `activeOfferIds` feeds the `batchLoadedOffers` memo — so an unstable reference rebuilt
  // every mapped offer object, which defeated `memo(ChatHotelCarouselCard)`, re-ran
  // `useOfferPackagePrice`/`useSearchPrices` for every card on screen, and re-fired a
  // product impression per card (patched at the effect's dep list; this is the cause).
  const stableOriginalOfferIds = useStableObjectReference(originalOfferIds)

  const activeOfferIds = useMemo(() => {
    if (shouldUseHotelSearchResults) return nlsOfferList.offerIds
    if (shouldFilterExperienceResults) {
      return stableOriginalOfferIds.filter((offerId) => availableExperienceIds.has(offerId))
    }
    return stableOriginalOfferIds
  }, [
    availableExperienceIds,
    nlsOfferList.offerIds,
    stableOriginalOfferIds,
    shouldFilterExperienceResults,
    shouldUseHotelSearchResults,
  ])

  const shouldUseCruiseOffers = cardType === 'cruise'
  const cruiseOffers = useCruiseCarouselOffers(activeOfferIds, shouldUseCruiseOffers)
  // Assistant-provided offers and all flexible searches use the detail DTO priced for
  // the current filters. Edited exact-date searches retain the live Redux rate path.
  const shouldUseOfferDetailBatch = cardType === 'hotel' && (!hasUserEditedFilters || dateMode === DATE_SEARCH_OPTION_IDS.FLEXIBLE)
  const offerDetailBatch = useOfferDetailBatch(
    shouldUseOfferDetailBatch ? activeOfferIds : [],
    {
      checkIn: dateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC ? checkIn?.format(ISO_DATE_FORMAT) : undefined,
      checkOut: dateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC ? checkOut?.format(ISO_DATE_FORMAT) : undefined,
      flexibleMonths: dateMode === DATE_SEARCH_OPTION_IDS.FLEXIBLE ? flexibleMonths : undefined,
      flexibleNights: dateMode === DATE_SEARCH_OPTION_IDS.FLEXIBLE ? flexibleNights : undefined,
      occupancy: rooms,
    },
  )
  const [genericLoadedOffers, genericOffersFetching] = useOffers<App.AnyOffer>(
    shouldUseCruiseOffers || shouldUseOfferDetailBatch ? [] : activeOfferIds,
  )
  // Mapped in `activeOfferIds` order rather than response order, so the carousel always
  // renders the offers in the order the assistant recommended them.
  const batchLoadedOffers = useMemo(() => {
    const detailsByOfferId = new Map<string, BatchOfferDetail>()
    for (const result of offerDetailBatch.results) {
      if (result.status === 'ok') detailsByOfferId.set(result.offerId, result.detail)
    }
    return activeOfferIds.flatMap((offerId) => {
      const detail = detailsByOfferId.get(offerId)
      return detail ? [mapOfferDetailToCarouselOffer(detail)] : []
    })
  }, [activeOfferIds, offerDetailBatch.results])

  let allLoadedOffers: Array<App.AnyOffer> = genericLoadedOffers
  let offersFetching = genericOffersFetching
  if (shouldUseCruiseOffers) {
    allLoadedOffers = cruiseOffers.offers
    offersFetching = cruiseOffers.fetching
  } else if (shouldUseOfferDetailBatch) {
    allLoadedOffers = batchLoadedOffers
    offersFetching = offerDetailBatch.isLoading
  }

  // An offer id is "resolved" once it has either loaded or errored. `useOffers` silently
  // drops errored/missing ids, so a plain `loaded.length === requested.length` check never
  // completes if the API omits or fails a single offer — the carousel then loads forever.
  // Counting errored ids as resolved (mirroring the cruise path) makes the check terminal.
  const offerErrors = useAppSelector((state) => state.offer.offerErrors)
  const loadedOfferIds = useMemo(
    () => new Set(allLoadedOffers.map((offer) => offer.id)),
    [allLoadedOffers],
  )
  const genericResolvedCount = useMemo(
    () => activeOfferIds.reduce(
      (count, offerId) => count + (loadedOfferIds.has(offerId) || offerErrors[offerId] != null ? 1 : 0),
      0,
    ),
    [activeOfferIds, loadedOfferIds, offerErrors],
  )
  // The batch reports its own per-offer failures inline, and those never reach Redux's
  // `offerErrors`, so the batch path needs its own count. Every id the response mentions —
  // `ok` or `error` — is resolved; anything else is still pending.
  //
  // For an id the endpoint OMITS from its response, "still pending" has no backstop.
  // `useOfferDetailBatch` drops every id it requested from `inFlightIds` once the response
  // lands, mentioned or not, so an omitted id ends up neither cached nor in flight.
  //
  // While the assistant is still streaming that self-heals: the next appended id re-runs the
  // effect, and `missingIds` picks the omitted one back up along with it. It is the TERMINAL
  // state that hangs — once the list stops changing, nothing re-runs the effect (its deps are
  // ids/filters/region only), so the id stays unresolved, `allOffersResolved` stays false and
  // `isFullyLoaded` leaves the carousel on skeletons indefinitely.
  //
  // Deliberate, and unreachable today: `getOfferDetailBatch` is `Promise.allSettled` plus a
  // per-offer catch, so it answers 1:1 and reports an id it cannot serve as an `error` entry
  // rather than omitting it. If that ever stops holding, this is the predicate that needs a
  // timeout or a treat-unmentioned-as-failed rule, not a retry.
  const batchResolvedOfferIds = useMemo(
    () => new Set(offerDetailBatch.results.map((result) => result.offerId)),
    [offerDetailBatch.results],
  )
  // A failed offer never gets a mapped `App.AnyOffer` (there's no offer to map), so this is
  // the only record that its id was ASKED for and answered with a failure — as opposed to
  // still being in flight. `filteredOfferIds` below uses exactly that distinction: a failed
  // id is dropped, a pending one keeps its slot. Empty outside the batch path, since
  // `offerDetailBatch.results` is `[]` there.
  const batchUnavailableReasonsByOfferId = useMemo(() => {
    const map = new Map<string, BatchUnavailableReason>()
    for (const result of offerDetailBatch.results) {
      if (result.status === 'error') map.set(result.offerId, result.reason)
    }
    return map
  }, [offerDetailBatch.results])
  const batchResolvedCount = useMemo(
    () => activeOfferIds.reduce(
      (count, offerId) => count + (batchResolvedOfferIds.has(offerId) ? 1 : 0),
      0,
    ),
    [activeOfferIds, batchResolvedOfferIds],
  )
  let resolvedOfferCount = genericResolvedCount
  if (shouldUseCruiseOffers) {
    resolvedOfferCount = cruiseOffers.resolvedCount
  } else if (shouldUseOfferDetailBatch) {
    resolvedOfferCount = batchResolvedCount
  }
  // Note: this is `true` for an empty id list so that empty filter results settle into the
  // empty state (rather than loading forever). Branches that must have at least one offer
  // keep their own explicit `activeOfferIds.length > 0` guard.
  const allOffersResolved = resolvedOfferCount === activeOfferIds.length

  // NLS doesn't enforce room capacity constraints, so we verify each offer
  // can accommodate the requested occupancy client-side.
  const offers = useMemo(() => {
    if (!shouldUseHotelSearchResults) return allLoadedOffers
    return allLoadedOffers.filter(offer => {
      if (isLEHotel(offer)) {
        if (!offer.packages?.length) return true
        return rooms.every((room: App.Occupants) =>
          offer.packages.some(pkg =>
            'roomRate' in pkg && pkg.roomRate?.capacities?.length
              ? allRoomsOccupantsFitRoomCapacity(room, pkg.roomRate.capacities, offer)
              : true,
          ),
        )
      }
      if (isBedbankOffer(offer)) {
        if (!offer.packages?.length) return true
        return rooms.every((room: App.Occupants) =>
          offer.packages.some(pkg =>
            pkg.capacities?.combinations?.length
              ? allRoomsOccupantsFitRoomCapacity(room, pkg.capacities.combinations, offer as App.BedbankOffer)
              : true,
          ),
        )
      }
      return true
    })
  }, [allLoadedOffers, rooms, shouldUseHotelSearchResults])

  // An offer the batch could not fetch is DROPPED, not rendered as a reason-bearing card.
  // This reverses B6 knowingly.
  //
  // TRP-4414's "show it, don't hide it" convention is about facts concerning the OFFER — a
  // sold-out room is something the customer needs told. Three of the four batch reasons are
  // not that: `timeout` and `upstream_error` are ours by construction, and `unavailable` is
  // `classifyFailure`'s default for an unclassified throw (an internal error, despite the
  // name). For those, a contentless "Price unavailable" card states something false about a
  // product that is usually perfectly bookable — often directly under assistant prose naming
  // that hotel with a price — and `retry: false` makes it stick for the life of the message.
  //
  // `not_found` IS a fact about the offer: with no `offerTypes` sent, svc-trip only raises it
  // after svc-public-offer 404s AND the bedbank lookup misses, including a dateless retry
  // that exists so a sold-out-for-these-dates property does not land here. It means the offer
  // is genuinely gone. It is hidden anyway, because the tile had no name, no image and no
  // working click target, so it could never convey "this one has been withdrawn" — but the
  // cost is real and different in kind: the prose above keeps recommending an offer that no
  // longer exists, and now nothing on screen contradicts it. Feeding that back into the
  // conversation is the proper fix and is not this change.
  //
  // Genuine unavailability is untouched either way: it arrives as `status: 'ok'` with the
  // price legs stripped inside the detail, so a full named card still renders via
  // ChatHotelCarouselPrice showing "Sold Out"/"Unavailable".
  //
  // What stays in the list is every id NOT known to have failed — resolved and still-pending
  // alike, in the assistant's order. The carousel renders one slot per id (card once mapped,
  // placeholder until then), so a card never moves once it lands: ids resolve out of order
  // and a trailing block of placeholders would make every late arrival shove its neighbours
  // sideways. Deduped because the streamed list can repeat an id, and `useOfferDetailBatch`
  // dedupes its own queries — two slots for one offer would be one card and one placeholder
  // that never fills.
  // When the typeahead finishes but can't resolve a location, treat as loaded
  // (empty) rather than showing loading skeletons indefinitely.
  const isFullyLoaded = useMemo(() => {
    if (shouldFallbackToOriginalHotelOffers) {
      return !offersFetching && activeOfferIds.length > 0 && allOffersResolved
    }

    if (shouldUseHotelSearchResults) {
      // A failed NLS offer-list stream is terminal: `fetching` can stay true forever, so
      // settle into the (empty) results state instead of loading indefinitely.
      if (nlsOfferList.error) return true
      return (typeaheadResolved && !extractedPlaceIds?.length) ||
        (!!nlsFilters && !nlsOfferList.fetching && !offersFetching && allOffersResolved)
    }

    if (shouldSearchHotelResults) {
      return false
    }

    if (shouldFilterExperienceResults) {
      // A failed availability lookup never populates the list, so treat it as terminal.
      if (experienceAvailabilityList.error) return true
      return hasExperienceAvailabilityList &&
        !experienceAvailabilityList.fetching &&
        !offersFetching &&
        allOffersResolved
    }

    if (shouldUseCruiseOffers) {
      return !offersFetching && activeOfferIds.length > 0 && allOffersResolved
    }

    // A failed batch request never resolves a single offer id, so — like the NLS and
    // experience branches above — treat it as terminal rather than loading forever.
    // `!isLoading` matters when the list is served by more than one request (an append
    // during streaming, or a reconnect refetch): `error` is truthy as soon as ANY query
    // fails, so without it one early failure declared the whole carousel loaded, dropped
    // the skeletons, and let the still-fetching cards pop in afterwards.
    if (shouldUseOfferDetailBatch && offerDetailBatch.error && !offerDetailBatch.isLoading) return true

    return !offersFetching && activeOfferIds.length > 0 && allOffersResolved
  }, [
    activeOfferIds.length,
    allOffersResolved,
    experienceAvailabilityList.error,
    experienceAvailabilityList.fetching,
    extractedPlaceIds?.length,
    hasExperienceAvailabilityList,
    nlsFilters,
    nlsOfferList.error,
    nlsOfferList.fetching,
    offerDetailBatch.error,
    offerDetailBatch.isLoading,
    offersFetching,
    shouldFallbackToOriginalHotelOffers,
    shouldFilterExperienceResults,
    shouldSearchHotelResults,
    shouldUseHotelSearchResults,
    shouldUseCruiseOffers,
    shouldUseOfferDetailBatch,
    typeaheadResolved,
  ])

  const mappedOfferIds = useMemo(() => new Set(offers.map(offer => offer.id)), [offers])

  const filteredOfferIds = useMemo(() => {
    if (!shouldUseOfferDetailBatch) return offers.map(offer => offer.id)
    const uniqueOfferIds = Array.from(new Set(activeOfferIds))
    // Once the carousel has settled, only offers that actually mapped may hold a slot. A
    // WHOLE-request failure resolves no per-offer entries at all, so nothing is marked
    // failed — without this branch every id would keep a placeholder that can never fill,
    // and `showEmptyState` below would never fire because the list is not empty.
    if (isFullyLoaded) return uniqueOfferIds.filter((offerId) => mappedOfferIds.has(offerId))
    return uniqueOfferIds.filter((offerId) => !batchUnavailableReasonsByOfferId.has(offerId))
  }, [activeOfferIds, batchUnavailableReasonsByOfferId, isFullyLoaded, mappedOfferIds, offers, shouldUseOfferDetailBatch])

  // Progressive reveal, batch path only. `filteredOfferIds` now carries pending ids too, so
  // this skeleton-ONLY view is reduced to the one case with nothing to lay out: no ids at
  // all, i.e. the carousel is still streaming its first id. Everything after that renders as
  // slots, each filling in place. Every other source keeps `!isFullyLoaded`, unchanged.
  //
  // This is what svc-trip's 6000ms per-offer budget leans on — but only together with
  // `useOfferDetailBatch` sending one offer per request. Rendering alone is not enough: a
  // request carrying several ids cannot answer until its slowest offer settles, so the
  // budget would still be paid by every card in it.
  const showLoadingCards = shouldUseOfferDetailBatch
    ? !isFullyLoaded && filteredOfferIds.length === 0
    : !isFullyLoaded
  // `shouldUseOfferDetailBatch` added so a batch where EVERY offer failed reaches the empty
  // state rather than rendering a heading, chips and a blank body — the pre-edit path
  // satisfied neither of the other two conditions, so it had no empty state at all.
  const showEmptyState = isFullyLoaded &&
    filteredOfferIds.length === 0 &&
    (shouldUseHotelSearchResults || shouldFilterExperienceResults || shouldUseOfferDetailBatch)

  return {
    offers,
    offerDetails: offerDetailBatch.results,
    hotelSearchFilters: shouldUseHotelSearchResults ? nlsFilters : undefined,
    listingDestinationId: listingItemData?.location?.input === searchQuery ? listingPlaceIds?.[0] : undefined,
    resolveListingDestination,
    isListingDestinationResolved: !!listingDropdownData?.isComplete && listingDropdownData.completedInput === searchQuery,
    hasListingDestinationError: !!listingDropdownData?.streamingError,
    hasSearchError: shouldUseHotelSearchResults && !!nlsOfferList.error,
    // Provenance of `offers`, so consumers can't drift from the condition that actually chose
    // the data source: true means every offer above carries svc-trip's already-computed price.
    isOfferDetailSource: shouldUseOfferDetailBatch,
    filteredOfferIds,
    // Batch-only per-offer failure reasons, keyed by offer id — empty everywhere else.
    // Still published even though a failed offer is no longer rendered: this is the signal
    // svc-trip's per-offer contract carries, and dropping it would make the hiding
    // indistinguishable from an offer the assistant never recommended.
    unavailableReasonsByOfferId: batchUnavailableReasonsByOfferId,
    offersFetching,
    isFullyLoaded,
    showLoadingCards,
    showEmptyState,
    typeaheadId,
  }
}
